/**
 * Study Plan Hooks
 * React Query hooks for study plan management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { getAuthTokenOrThrow } from '@/lib/auth-token';
import { useAuth } from '@/lib/auth';
import { AI_BASE_URL } from '@/lib/ai-base-url';
import type {
  StudyPlan,
  StudyPlanStatus,
  StudyPlanType,
  StudyPlanData,
  GenerationMetadata,
  GeneratePlanRequest,
} from '@/types/study-plan';

// ============================================================================
// DATABASE ROW TYPE
// ============================================================================

interface StudyPlanRow {
  id: string;
  user_id: string;
  title: string | null;
  description: string | null;
  exam_date: string | null;
  start_date: string | null;
  end_date: string | null;
  subjects: string[] | null;
  status: string | null;
  plan_type: string | null;
  plan_data: unknown | null;
  generation_metadata: unknown | null;
  total_tasks: number | null;
  completed_tasks: number | null;
  progress_percentage: number | null;
  created_at: string | null;
  updated_at: string | null;
}

// Helper to convert database row to StudyPlan
function toPlan(row: StudyPlanRow): StudyPlan {
  return {
    id: row.id,
    user_id: row.user_id,
    title: row.title || 'Study Plan',
    description: row.description,
    exam_date: row.exam_date,
    start_date: row.start_date || new Date().toISOString().split('T')[0],
    end_date: row.end_date,
    subjects: row.subjects || [],
    status: (row.status as StudyPlanStatus) || 'active',
    plan_type: (row.plan_type as StudyPlanType) || 'general',
    plan_data: row.plan_data as StudyPlanData | null,
    generation_metadata: row.generation_metadata as GenerationMetadata | null,
    total_tasks: row.total_tasks || 0,
    completed_tasks: row.completed_tasks || 0,
    progress_percentage: row.progress_percentage || 0,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const studyPlanKeys = {
  all: ['study-plans'] as const,
  list: (userId: string) => [...studyPlanKeys.all, 'list', userId] as const,
  active: (userId: string) => [...studyPlanKeys.all, 'active', userId] as const,
  detail: (planId: string) => [...studyPlanKeys.all, 'detail', planId] as const,
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch all study plans for the current user
 */
export function useStudyPlans() {
  const { user } = useAuth();

  return useQuery({
    queryKey: studyPlanKeys.list(user?.id ?? ''),
    queryFn: async (): Promise<StudyPlan[]> => {
      if (!user?.id) return [];

      const { data, error } = await (supabase
        .from('study_plans' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50) as unknown as Promise<{
        data: StudyPlanRow[] | null;
        error: any;
      }>);

      if (error) throw error;
      return (data ?? []).map(toPlan);
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch the user's active study plan
 */
export function useActiveStudyPlan() {
  const { user } = useAuth();

  return useQuery({
    queryKey: studyPlanKeys.active(user?.id ?? ''),
    queryFn: async (): Promise<StudyPlan | null> => {
      if (!user?.id) return null;

      const { data, error } = await (supabase
        .from('study_plans' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle() as unknown as Promise<{
        data: StudyPlanRow | null;
        error: any;
      }>);

      if (error) throw error;
      return data ? toPlan(data) : null;
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch a single study plan by ID
 * Security: Filters by both planId AND user_id to prevent unauthorized access
 */
export function useStudyPlan(planId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: studyPlanKeys.detail(planId ?? ''),
    queryFn: async (): Promise<StudyPlan | null> => {
      if (!planId || !user?.id) return null;

      const { data, error } = await (supabase
        .from('study_plans' as any)
        .select('*')
        .eq('id', planId)
        .eq('user_id', user.id) // Security: Ensure user can only access their own plans
        .maybeSingle() as unknown as Promise<{
        data: StudyPlanRow | null;
        error: any;
      }>);

      if (error) throw error;
      return data ? toPlan(data) : null;
    },
    enabled: !!planId && !!user?.id,
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new study plan
 */
export function useCreateStudyPlan() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      title: string;
      subjects: string[];
      exam_date?: string;
      plan_type?: StudyPlanType;
      description?: string;
    }): Promise<StudyPlan> => {
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await (supabase
        .from('study_plans' as any)
        .insert({
          user_id: user.id,
          title: input.title,
          description: input.description || null,
          subjects: input.subjects,
          exam_date: input.exam_date || null,
          start_date: new Date().toISOString().split('T')[0],
          plan_type: input.plan_type || 'general',
          status: 'active',
          total_tasks: 0,
          completed_tasks: 0,
          progress_percentage: 0,
        })
        .select()
        .single() as unknown as Promise<{ data: StudyPlanRow; error: any }>);

      if (error) throw error;
      return toPlan(data);
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.list(user.id) });
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.active(user.id) });
      }
    },
  });
}

/**
 * Update study plan status
 */
export function useUpdateStudyPlanStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      planId,
      status,
    }: {
      planId: string;
      status: StudyPlanStatus;
    }): Promise<StudyPlan> => {
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await (supabase
        .from('study_plans' as any)
        .update({
          status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', planId)
        .eq('user_id', user.id) // Ensure user can only update their own plans
        .select()
        .single() as unknown as Promise<{ data: StudyPlanRow; error: any }>);

      if (error) throw error;
      return toPlan(data);
    },
    onSuccess: (data) => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.list(user.id) });
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.active(user.id) });
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.detail(data.id) });
      }
    },
  });
}

/**
 * Update study plan data
 */
export function useUpdateStudyPlan() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      planId,
      title,
      description,
      exam_date,
      plan_data,
    }: {
      planId: string;
      title?: string;
      description?: string;
      exam_date?: string;
      plan_data?: StudyPlanData;
    }): Promise<StudyPlan> => {
      if (!user?.id) throw new Error('Not authenticated');

      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (title !== undefined) updateData.title = title;
      if (description !== undefined) updateData.description = description;
      if (exam_date !== undefined) updateData.exam_date = exam_date;
      if (plan_data !== undefined) updateData.plan_data = plan_data;

      const { data, error } = await (supabase
        .from('study_plans' as any)
        .update(updateData)
        .eq('id', planId)
        .eq('user_id', user.id) // Ensure user can only update their own plans
        .select()
        .single() as unknown as Promise<{ data: StudyPlanRow; error: any }>);

      if (error) throw error;
      return toPlan(data);
    },
    onSuccess: (data) => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.list(user.id) });
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.detail(data.id) });
      }
    },
  });
}

/**
 * Delete a study plan
 */
export function useDeleteStudyPlan() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (planId: string): Promise<void> => {
      if (!user?.id) throw new Error('Not authenticated');

      const { error } = await (supabase
        .from('study_plans' as any)
        .delete()
        .eq('id', planId)
        .eq('user_id', user.id) as unknown as Promise<{ error: any }>); // Ensure user can only delete their own plans

      if (error) throw error;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.list(user.id) });
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.active(user.id) });
      }
    },
  });
}

/**
 * Generate a new study plan using AI
 */
export function useGenerateStudyPlan() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (request: GeneratePlanRequest): Promise<StudyPlan> => {
      if (!user?.id) throw new Error('Not authenticated');

      const token = await getAuthTokenOrThrow();

      // Call MIG study-plan service directly
      const response = await fetch(`${AI_BASE_URL}/study-plan/generate-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to generate plan (${response.status})`);
      }

      const result = await response.json();
      if (!result.success || !result.plan) {
        throw new Error(result.error || 'Failed to generate plan');
      }

      return result.plan as StudyPlan;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.list(user.id) });
        queryClient.invalidateQueries({ queryKey: studyPlanKeys.active(user.id) });
      }
    },
  });
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Check if user has an active study plan
 */
export function useHasActiveStudyPlan(): boolean {
  const { data: activePlan } = useActiveStudyPlan();
  return !!activePlan;
}

/**
 * Get today's activities from the active plan
 */
export function useTodaysActivities() {
  const { data: activePlan, isLoading } = useActiveStudyPlan();

  if (isLoading || !activePlan?.plan_data) {
    return { activities: [], isLoading };
  }

  const today = new Date().toISOString().split('T')[0];

  // Find today's activities from the plan
  for (const week of activePlan.plan_data.weeks) {
    const todayData = week.days.find((day) => day.date === today);
    if (todayData) {
      return { activities: todayData.activities, isLoading: false };
    }
  }

  return { activities: [], isLoading: false };
}

/**
 * Get study plan statistics
 */
export function useStudyPlanStats() {
  const { data: plans = [], isLoading } = useStudyPlans();
  const { data: activePlan } = useActiveStudyPlan();

  return {
    totalPlans: plans.length,
    activePlans: plans.filter((p) => p.status === 'active').length,
    completedPlans: plans.filter((p) => p.status === 'completed').length,
    currentProgress: activePlan?.progress_percentage || 0,
    isLoading,
  };
}
